import { useState } from 'react';

/**
 * The value of a date or number input, or null while it holds something the browser cannot
 * read (a half-typed date, "1,5" where "." is expected). Saving null would erase the stored value.
 */
export function inputValue(el: HTMLInputElement): string | null {
  return el.validity.badInput ? null : el.value;
}

/**
 * What a date or number field saves on blur: the new value, or null = save nothing. Nothing when it is
 * unchanged, or when the browser cannot read it (a half-typed date, "1,5"): then the stored value is put back.
 */
export function changedValue(el: HTMLInputElement, stored: string): string | null {
  const v = inputValue(el);
  if (v === null) {
    el.value = stored;
    return null;
  }
  return v === stored ? null : v;
}

/**
 * A list saved whole (grants, roles) as it will be after the save sent: the next change builds on it, not
 * on `stored` (a reload that fails keeps that). New data from the server drops it; a failed save goes back
 * to the list sent before it.
 *   list         what to show and to build the next change on
 *   own          true while `list` is the one sent, not `stored`
 *   begin(next)  marks `next` as sent; returns what a failed save calls
 */
export function useSentList(stored: readonly string[]) {
  const from = stored.join(',');
  const [sent, setSent] = useState<{ from: string; list: readonly string[] } | null>(null);
  if (sent && sent.from !== from) setSent(null);
  const own = sent?.from === from;
  const begin = (next: readonly string[]) => {
    const prev = sent;
    setSent({ from, list: next });
    return () => setSent(prev);
  };
  return { list: own ? sent.list : stored, own, begin };
}
